import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const activeIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:linear-gradient(135deg,#d97706,#f59e0b);border:3px solid #fcd34d;box-shadow:0 0 12px rgba(217,119,6,0.5),0 2px 8px rgba(0,0,0,0.3);display:flex;align-items:center;justify-content:center"><div style="width:8px;height:8px;background:#fff;border-radius:50%"></div></div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const standbyIcon = new L.DivIcon({
  className: '',
  html: `<div style="width:22px;height:22px;border-radius:50%;background:rgba(120,80,20,0.4);border:2px solid rgba(217,119,6,0.3);box-shadow:0 0 6px rgba(217,119,6,0.2);display:flex;align-items:center;justify-content:center"><div style="width:6px;height:6px;background:rgba(217,119,6,0.5);border-radius:50%"></div></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

function FitBounds({ guards }) {
  const map = useMap();
  useEffect(() => {
    if (guards.length > 0) {
      const bounds = L.latLngBounds(guards.map(g => [g.lat, g.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [guards, map]);
  return null;
}

export default function EscortGuardAdmin() {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [guards, setGuards] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('map');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newGuard, setNewGuard] = useState({ name: '', city: '', country: '', lat: '', lng: '', instrument: "Ram's Horn", status: 'active' });

  useEffect(() => {
    if (!isAuthenticated()) navigate('/login');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const fetchGuards = async () => {
      try {
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/api/escort-guard/guards`, { headers, credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setGuards(data.guards || []);
          setStats(data.stats || null);
        }
      } catch { /* silent */ }
      setLoading(false);
    };
    fetchGuards();
  }, [token]);

  const handleAdd = async () => {
    if (!newGuard.name || !newGuard.city) return;
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${API_URL}/api/escort-guard/guards`, {
        method: 'POST', headers, credentials: 'include',
        body: JSON.stringify({ ...newGuard, lat: parseFloat(newGuard.lat) || 0, lng: parseFloat(newGuard.lng) || 0 }),
      });
      if (res.ok) {
        setShowAdd(false);
        setNewGuard({ name: '', city: '', country: '', lat: '', lng: '', instrument: "Ram's Horn", status: 'active' });
        // Refresh
        const res2 = await fetch(`${API_URL}/api/escort-guard/guards`, { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' });
        if (res2.ok) { const d = await res2.json(); setGuards(d.guards || []); setStats(d.stats || null); }
      }
    } catch { /* silent */ }
  };

  const filtered = guards.filter(g =>
    !search || g.name?.toLowerCase().includes(search.toLowerCase()) ||
    g.city?.toLowerCase().includes(search.toLowerCase()) ||
    g.country?.toLowerCase().includes(search.toLowerCase())
  );

  const INSTRUMENTS = { "Ram's Horn": '#d97706', "Silver Shofar": '#94a3b8', "Yemenite Shofar": '#a855f7', "Kudu Horn": '#22c55e' };

  return (
    <div className="min-h-screen" style={{ background: '#060503' }}>
      <Header />

      <div className="max-w-7xl mx-auto px-4 pt-24 pb-16">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <p className="text-[9px] tracking-[0.4em] uppercase font-mono text-amber-500/60 mb-1">Private Admin Registry</p>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold" style={{
              background: 'linear-gradient(180deg, #fcd34d 0%, #d97706 50%, #92400e 100%)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Kingdom Escort Guard
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <button onClick={() => setShowAdd(true)} data-testid="btn-add-guard"
              className="px-4 py-2 rounded-lg text-xs font-serif font-semibold transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #b45309, #d97706)', color: '#fff' }}>
              + Add Guard
            </button>
            <div className="flex rounded-lg overflow-hidden border" style={{ borderColor: 'rgba(120,80,20,0.15)' }}>
              {['map', 'list'].map(v => (
                <button key={v} onClick={() => setView(v)} data-testid={`btn-view-${v}`}
                  className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider transition-colors"
                  style={{ background: view === v ? 'rgba(217,119,6,0.15)' : 'rgba(10,8,4,0.5)', color: view === v ? '#fbbf24' : 'rgba(217,119,6,0.5)' }}>
                  {v}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats Bar */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="guard-stats">
            {[
              { label: 'Total Guards', value: stats.total, color: '#fbbf24' },
              { label: 'Active', value: stats.active, color: '#10b981' },
              { label: 'Standby', value: stats.standby, color: '#f59e0b' },
              { label: 'Countries', value: stats.countries, color: '#06b6d4' },
            ].map(s => (
              <div key={s.label} className="rounded-xl border p-3 text-center" style={{ background: 'rgba(15,10,5,0.6)', borderColor: 'rgba(120,80,20,0.1)' }}>
                <p className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.value}</p>
                <p className="text-[9px] font-mono uppercase tracking-wider text-amber-400/70">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="mb-4">
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, city, or country..."
            data-testid="guard-search"
            className="w-full sm:w-80 px-4 py-2.5 rounded-lg border text-sm font-serif text-amber-100 placeholder:text-amber-500/50 focus:outline-none focus:border-amber-600/40"
            style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.15)' }}
          />
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-8 h-8 border-2 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-amber-400/60 font-serif">Loading registry...</p>
          </div>
        ) : view === 'map' ? (
          /* Map View */
          <div className="rounded-xl overflow-hidden border relative" style={{ borderColor: 'rgba(120,80,20,0.15)', height: '500px' }} data-testid="guard-map">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: '100%', width: '100%', background: '#0a0804' }}
              scrollWheelZoom={true}
              attributionControl={false}
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              />
              <FitBounds guards={filtered} />
              {filtered.map(guard => (
                <Marker
                  key={guard.guard_id}
                  position={[guard.lat, guard.lng]}
                  icon={guard.status === 'active' ? activeIcon : standbyIcon}
                >
                  <Popup>
                    <div style={{ minWidth: '180px', fontFamily: 'serif', color: '#1a1a1a' }}>
                      <p style={{ fontWeight: 'bold', fontSize: '14px', marginBottom: '4px' }}>{guard.name}</p>
                      <p style={{ fontSize: '12px', color: '#666' }}>{guard.city}, {guard.country}</p>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '6px', fontSize: '11px' }}>
                        <span style={{ padding: '2px 8px', borderRadius: '12px', background: guard.status === 'active' ? '#dcfce7' : '#fef3c7', color: guard.status === 'active' ? '#166534' : '#92400e' }}>
                          {guard.status}
                        </span>
                        <span style={{ color: INSTRUMENTS[guard.instrument] || '#888' }}>
                          {guard.instrument}
                        </span>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>

            {/* Map Legend */}
            <div className="absolute bottom-4 left-4 z-[1000] px-3 py-2 rounded-lg border"
              style={{ background: 'rgba(10,8,4,0.9)', borderColor: 'rgba(120,80,20,0.15)', backdropFilter: 'blur(8px)' }}>
              <p className="text-[9px] font-mono uppercase tracking-wider text-amber-300/80 mb-1.5">Legend</p>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-3 h-3 rounded-full" style={{ background: 'linear-gradient(135deg, #d97706, #f59e0b)', border: '1.5px solid #fcd34d' }} />
                <span className="text-[11px] text-amber-200/80 font-serif">Active</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ background: 'rgba(120,80,20,0.4)', border: '1.5px solid rgba(217,119,6,0.3)' }} />
                <span className="text-[11px] text-amber-300/70 font-serif">Standby</span>
              </div>
            </div>
          </div>
        ) : (
          /* List View */
          <div className="rounded-xl border overflow-hidden" style={{ background: 'rgba(15,10,5,0.6)', borderColor: 'rgba(120,80,20,0.1)' }} data-testid="guard-list">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(120,80,20,0.1)' }}>
                    {['Name', 'Location', 'Instrument', 'Status', 'Joined'].map(h => (
                      <th key={h} className="text-left px-4 py-3 text-[9px] font-mono uppercase tracking-wider text-amber-400/70">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(guard => (
                    <tr key={guard.guard_id} className="hover:bg-amber-900/5 transition-colors" style={{ borderBottom: '1px solid rgba(60,40,10,0.06)' }}>
                      <td className="px-4 py-3 text-xs font-serif text-amber-200">{guard.name}</td>
                      <td className="px-4 py-3 text-xs font-serif text-amber-400/60">{guard.city}, {guard.country}</td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-serif" style={{ color: INSTRUMENTS[guard.instrument] || '#888' }}>{guard.instrument}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[9px] font-mono px-2 py-0.5 rounded-full" style={{
                          background: guard.status === 'active' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)',
                          color: guard.status === 'active' ? '#10b981' : '#f59e0b',
                          border: `1px solid ${guard.status === 'active' ? 'rgba(16,185,129,0.2)' : 'rgba(245,158,11,0.2)'}`,
                        }}>{guard.status}</span>
                      </td>
                      <td className="px-4 py-3 text-[10px] font-mono text-amber-400/50">{guard.joined}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Add Guard Modal */}
        {showAdd && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowAdd(false)} />
            <div className="relative z-10 w-full max-w-md rounded-xl border p-6" data-testid="add-guard-modal"
              style={{ background: '#0c0a06', borderColor: 'rgba(120,80,20,0.2)' }}>
              <h3 className="text-lg font-serif font-bold text-amber-200 mb-4">Register New Guard</h3>
              <div className="space-y-3">
                {[
                  { key: 'name', label: 'Name', type: 'text' },
                  { key: 'city', label: 'City', type: 'text' },
                  { key: 'country', label: 'Country', type: 'text' },
                  { key: 'lat', label: 'Latitude', type: 'number' },
                  { key: 'lng', label: 'Longitude', type: 'number' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-[9px] font-mono uppercase tracking-wider text-amber-700/50 mb-1 block">{f.label}</label>
                    <input type={f.type} value={newGuard[f.key]} onChange={e => setNewGuard(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2 rounded-lg border text-sm font-serif text-amber-100 focus:outline-none focus:border-amber-600/40"
                      style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.15)' }} />
                  </div>
                ))}
                <div>
                  <label className="text-[9px] font-mono uppercase tracking-wider text-amber-700/50 mb-1 block">Instrument</label>
                  <select value={newGuard.instrument} onChange={e => setNewGuard(p => ({ ...p, instrument: e.target.value }))}
                    className="w-full px-3 py-2 rounded-lg border text-sm font-serif text-amber-100 focus:outline-none"
                    style={{ background: 'rgba(10,8,4,0.6)', borderColor: 'rgba(120,80,20,0.15)' }}>
                    {Object.keys(INSTRUMENTS).map(i => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-5">
                <button onClick={handleAdd} data-testid="btn-save-guard"
                  className="flex-1 px-4 py-2.5 rounded-lg text-sm font-serif font-bold"
                  style={{ background: 'linear-gradient(135deg, #b45309, #d97706)', color: '#fff' }}>
                  Save Guard
                </button>
                <button onClick={() => setShowAdd(false)}
                  className="px-4 py-2.5 rounded-lg text-sm font-serif border"
                  style={{ borderColor: 'rgba(120,80,20,0.2)', color: 'rgba(217,119,6,0.5)' }}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
